import {
  chainCommands,
  exitCode,
  lift,
  setBlockType,
  toggleMark,
  wrapIn,
} from "prosemirror-commands";
import { Fragment, NodeRange, type NodeType, Slice } from "prosemirror-model";
import { wrapInList } from "prosemirror-schema-list";
import {
  type Command,
  TextSelection,
  type Transaction,
} from "prosemirror-state";
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  deleteTable,
  isInTable,
} from "prosemirror-tables";
import { canJoin, liftTarget, ReplaceAroundStep } from "prosemirror-transform";
import type { EditorView } from "prosemirror-view";

import {
  createSourceFragment,
  formatSourceMarkdownImage,
  formatSourceMarkdownLink,
} from "./markdown/source-utils";
import { focusSchema } from "./schema";

export const runEditorCommand = (
  view: EditorView | undefined,
  command: Command,
) => {
  if (!view) {
    return false;
  }

  const result = command(view.state, view.dispatch, view);
  view.focus();
  return result;
};

const blockHasAncestor = (state: Parameters<Command>[0], nodeName: string) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === nodeName) {
      return true;
    }
  }

  return false;
};

const appendTrailingParagraph = (tr: Transaction) => {
  const paragraph = focusSchema.nodes.paragraph;
  const lastChild = tr.doc.lastChild;

  if (lastChild?.type === paragraph && lastChild.content.size === 0) {
    return tr;
  }

  return tr.insert(tr.doc.content.size, paragraph.create());
};

const withTrailingParagraph =
  (dispatch: Parameters<Command>[1]) => (tr: Transaction) => {
    dispatch?.(appendTrailingParagraph(tr));
  };

const toggleBlockquote: Command = (state, dispatch, view) => {
  if (blockHasAncestor(state, "blockquote")) {
    return lift(state, dispatch, view);
  }

  return wrapIn(focusSchema.nodes.blockquote)(
    state,
    dispatch ? withTrailingParagraph(dispatch) : undefined,
    view,
  );
};

const liftListItemWithoutScroll =
  (itemType: NodeType): Command =>
  (state, dispatch) => {
    const { $from, $to } = state.selection;
    const range = $from.blockRange(
      $to,
      (node) => node.childCount > 0 && node.firstChild?.type === itemType,
    );

    if (!range) {
      return false;
    }

    if (!dispatch) {
      return true;
    }

    if ($from.node(range.depth - 1).type === itemType) {
      const transaction = state.tr;
      const end = range.end;
      const endOfList = range.$to.end(range.depth);
      let nextRange = range;

      if (end < endOfList) {
        transaction.step(
          new ReplaceAroundStep(
            end - 1,
            endOfList,
            end,
            endOfList,
            new Slice(
              Fragment.from(itemType.create(null, range.parent.copy())),
              1,
              0,
            ),
            1,
            true,
          ),
        );
        nextRange = new NodeRange(
          transaction.doc.resolve(range.$from.pos),
          transaction.doc.resolve(endOfList),
          range.depth,
        );
      }

      const target = liftTarget(nextRange);

      if (target == null) {
        return false;
      }

      transaction.lift(nextRange, target);
      const $after = transaction.doc.resolve(
        transaction.mapping.map(end, -1) - 1,
      );

      if (
        canJoin(transaction.doc, $after.pos) &&
        $after.nodeBefore?.type === $after.nodeAfter?.type
      ) {
        transaction.join($after.pos);
      }

      dispatch(transaction);
      return true;
    }

    const transaction = state.tr;
    const list = range.parent;

    for (
      let pos = range.end, index = range.endIndex - 1;
      index > range.startIndex;
      index--
    ) {
      pos -= list.child(index).nodeSize;
      transaction.delete(pos - 1, pos + 1);
    }

    const $start = transaction.doc.resolve(range.start);
    const item = $start.nodeAfter;

    if (
      !item ||
      transaction.mapping.map(range.end) !== range.start + item.nodeSize
    ) {
      return false;
    }

    const atStart = range.startIndex === 0;
    const atEnd = range.endIndex === list.childCount;
    const parent = $start.node(-1);
    const indexBefore = $start.index(-1);

    if (
      !parent.canReplace(
        indexBefore + (atStart ? 0 : 1),
        indexBefore + 1,
        item.content.append(atEnd ? Fragment.empty : Fragment.from(list)),
      )
    ) {
      return false;
    }

    const start = $start.pos;
    const end = start + item.nodeSize;

    transaction.step(
      new ReplaceAroundStep(
        start - (atStart ? 1 : 0),
        end + (atEnd ? 1 : 0),
        start + 1,
        end - 1,
        new Slice(
          (atStart
            ? Fragment.empty
            : Fragment.from(list.copy(Fragment.empty))
          ).append(
            atEnd ? Fragment.empty : Fragment.from(list.copy(Fragment.empty)),
          ),
          atStart ? 0 : 1,
          atEnd ? 0 : 1,
        ),
        atStart ? 0 : 1,
      ),
    );
    dispatch(transaction);
    return true;
  };

const findAncestorDepth = (
  state: Parameters<Command>[0],
  nodeNames: Set<string>,
) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    if (nodeNames.has($from.node(depth).type.name)) {
      return depth;
    }
  }

  return null;
};

const toggleList =
  (nodeName: "bullet_list" | "ordered_list"): Command =>
  (state, dispatch, view) => {
    const listType = focusSchema.nodes[nodeName];
    const listAttrs =
      nodeName === "ordered_list" ? { order: 1, tight: true } : { tight: true };
    const listDepth = findAncestorDepth(
      state,
      new Set(["bullet_list", "ordered_list"]),
    );

    if (listDepth == null) {
      return wrapInList(listType, listAttrs)(state, dispatch, view);
    }

    const currentList = state.selection.$from.node(listDepth);

    if (currentList.type === listType) {
      return liftListItemWithoutScroll(focusSchema.nodes.list_item)(
        state,
        dispatch,
        view,
      );
    }

    if (dispatch) {
      const listPosition = state.selection.$from.before(listDepth);
      dispatch(
        state.tr.setNodeMarkup(
          listPosition,
          listType,
          nodeName === "ordered_list"
            ? { order: 1, tight: currentList.attrs.tight ?? true }
            : { tight: currentList.attrs.tight ?? true },
        ),
      );
    }

    return true;
  };

export const editorCommands = {
  blockquote: toggleBlockquote,
  bulletList: toggleList("bullet_list"),
  codeBlock: chainCommands(exitCode, (state, dispatch, view) =>
    setBlockType(focusSchema.nodes.code_block, { language: "" })(
      state,
      dispatch ? withTrailingParagraph(dispatch) : undefined,
      view,
    ),
  ),
  hardBreak: (state, dispatch) => {
    dispatch?.(
      state.tr.replaceSelectionWith(focusSchema.nodes.hard_break.create()),
    );
    return true;
  },
  heading: (level: number) =>
    setBlockType(focusSchema.nodes.heading, {
      level,
    }),
  horizontalRule: (state, dispatch) => {
    const tr = state.tr.replaceSelectionWith(
      focusSchema.nodes.horizontal_rule.create(),
    );

    dispatch?.(appendTrailingParagraph(tr));
    return true;
  },
  orderedList: toggleList("ordered_list"),
  paragraph: setBlockType(focusSchema.nodes.paragraph),
  strike: toggleMark(focusSchema.marks.strike),
  strong: toggleMark(focusSchema.marks.strong),
  em: toggleMark(focusSchema.marks.em),
  code: toggleMark(focusSchema.marks.code),
} satisfies Record<string, Command | ((level: number) => Command)>;

const replaceSelectionWithSource =
  (open: string, close = open, fallback = "text"): Command =>
  (state, dispatch) => {
    const { from, to } = state.selection;
    const selectedText = state.doc.textBetween(from, to);
    const inner = selectedText || fallback;
    const text = `${open}${inner}${close}`;
    const tr = state.tr.replaceWith(from, to, createSourceFragment(text));
    const selectionFrom = selectedText
      ? from + text.length
      : from + open.length;

    tr.setSelection(TextSelection.create(tr.doc, selectionFrom));
    dispatch?.(tr.scrollIntoView());
    return true;
  };

const insertSourceMarkdownLink =
  ({
    href,
    text,
    title,
  }: {
    href: string;
    text: string;
    title?: string;
  }): Command =>
  (state, dispatch) => {
    const markdown = formatSourceMarkdownLink({ href, text, title });
    const { from, to } = state.selection;
    const tr = state.tr.insertText(markdown, from, to);

    tr.setSelection(TextSelection.create(tr.doc, from + markdown.length));
    dispatch?.(tr.scrollIntoView());
    return true;
  };

const insertSourceMarkdownImage =
  ({
    alt = "Image",
    src,
    title,
  }: {
    alt?: string | null;
    src: string;
    title?: string | null;
  }): Command =>
  (state, dispatch) => {
    const markdown = formatSourceMarkdownImage({ alt, src, title });
    const { from, to } = state.selection;
    const tr = state.tr.insertText(markdown, from, to);

    tr.setSelection(TextSelection.create(tr.doc, from + markdown.length));
    dispatch?.(tr.scrollIntoView());
    return true;
  };

const setSourceBlockPrefix =
  (prefix: string, removablePattern?: RegExp): Command =>
  (state, dispatch) => {
    const { $from } = state.selection;

    if (!$from.parent.isTextblock) {
      return false;
    }

    const blockStart = $from.start();
    const blockText = $from.parent.textContent;
    const currentPrefix = removablePattern?.exec(blockText)?.[0] ?? "";
    const replacement = currentPrefix === prefix ? "" : prefix;
    const tr = state.tr.insertText(
      replacement,
      blockStart,
      blockStart + currentPrefix.length,
    );

    dispatch?.(tr.scrollIntoView());
    return true;
  };

const insertSourceSnippet =
  (snippet: string, caretOffset = snippet.length): Command =>
  (state, dispatch) => {
    const { from, to } = state.selection;
    const tr = state.tr.replaceWith(from, to, createSourceFragment(snippet));

    tr.setSelection(TextSelection.create(tr.doc, from + caretOffset));
    dispatch?.(tr.scrollIntoView());
    return true;
  };

export const editorSourceCommands = {
  blockquote: setSourceBlockPrefix("> ", /^>\s/),
  bulletList: setSourceBlockPrefix("- ", /^(\d+\.\s|[-+*]\s|- \[[ xX]\]\s)/),
  code: replaceSelectionWithSource("`", "`", "code"),
  codeBlock: replaceSelectionWithSource("```\n", "\n```", ""),
  em: replaceSelectionWithSource("*"),
  heading: (level: number) =>
    setSourceBlockPrefix(`${"#".repeat(level)} `, /^#{1,6}\s/),
  horizontalRule: insertSourceSnippet("\n\n---\n\n"),
  orderedList: setSourceBlockPrefix("1. ", /^(\d+\.\s|[-+*]\s|- \[[ xX]\]\s)/),
  paragraph: setSourceBlockPrefix("", /^(#{1,6}\s|>\s|[-+*]\s|\d+\.\s)/),
  strike: replaceSelectionWithSource("~~"),
  strong: replaceSelectionWithSource("**"),
} satisfies Record<string, Command | ((level: number) => Command)>;

export const insertSourceLink = insertSourceMarkdownLink;

export const insertSourceImage = insertSourceMarkdownImage;

export const insertSourceTable = (): Command =>
  insertSourceSnippet(
    ["| Header | Header |", "| --- | --- |", "| Cell | Cell |"].join("\n"),
  );

export const editorTableCommands = {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  deleteTable,
  isInTable: (state: Parameters<Command>[0]) => isInTable(state),
};

export const insertEmoji =
  (emoji: string): Command =>
  (state, dispatch) => {
    dispatch?.(state.tr.insertText(emoji).scrollIntoView());
    return true;
  };

export const insertMention =
  ({
    displayName,
    publicKey,
    trigger = "@",
    username,
  }: {
    displayName?: string | null;
    publicKey?: string | null;
    trigger?: "@" | "$";
    username: string;
  }): Command =>
  (state, dispatch) => {
    dispatch?.(
      state.tr
        .replaceSelectionWith(
          focusSchema.nodes.mention.create({
            displayName: displayName ?? null,
            publicKey: publicKey ?? null,
            trigger,
            username,
          }),
        )
        .insertText(" ")
        .scrollIntoView(),
    );
    return true;
  };

export const insertInlineImage =
  ({
    alt = "Image",
    src,
    title = null,
  }: {
    alt?: string | null;
    src: string;
    title?: string | null;
  }): Command =>
  (state, dispatch) => {
    dispatch?.(
      state.tr
        .replaceSelectionWith(
          focusSchema.nodes.image.create({
            alt,
            src,
            title,
          }),
        )
        .scrollIntoView(),
    );
    return true;
  };

export const insertLink =
  ({
    href,
    text,
    title,
  }: {
    href: string;
    text: string;
    title?: string;
  }): Command =>
  (state, dispatch) => {
    const linkMark = focusSchema.marks.link.create({
      href,
      title: title || null,
    });

    dispatch?.(
      state.tr
        .replaceSelectionWith(focusSchema.text(text || href, [linkMark]), false)
        .scrollIntoView(),
    );
    return true;
  };

export const insertTable = (): Command => (state, dispatch) => {
  const { table, table_cell, table_header, table_row } = focusSchema.nodes;
  const { from } = state.selection;
  const headerRow = table_row.create(null, [
    table_header.create(null, focusSchema.text("Header")),
    table_header.create(null, focusSchema.text("Header")),
  ]);
  const bodyRow = table_row.create(null, [
    table_cell.create(null, focusSchema.text("Cell")),
    table_cell.create(null, focusSchema.text("Cell")),
  ]);
  const tableNode = table.create(null, [headerRow, bodyRow]);
  const tr = state.tr.replaceSelectionWith(tableNode);
  let firstCellPosition: number | null = null;

  appendTrailingParagraph(tr);

  tr.doc.descendants((node, position) => {
    if (position < from - 1) {
      return true;
    }

    if (node.type === table_cell || node.type === table_header) {
      firstCellPosition = position + 1;
      return false;
    }

    return firstCellPosition == null;
  });

  if (firstCellPosition != null) {
    tr.setSelection(TextSelection.create(tr.doc, firstCellPosition));
  }

  dispatch?.(tr.scrollIntoView());
  return true;
};
